import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { restoreDeleted } from "src/utils/undoDelete";
import { rememberUndo } from "src/utils/lastUndo";
import { reloadKeepingPlace } from "src/utils/afterReload";

// A "Deleted" message with Undo: the record comes back as the Activity
// log's Undo would bring it, and the page reloads to show it, at the same
// place, saying so. Ctrl+Z does the same for a minute (georges-09, -31).
const UndoToast = ({ message, undo, closeToast, reload }) => {
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const press = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const restored = await restoreDeleted(undo);
      closeToast?.();
      const words = restored > 1 ? `${restored} restored. They’re back.` : "Restored. It’s back.";
      // A page that reads itself again in place says so now; otherwise the
      // page reloads at the same place and says it there.
      if (reload) {
        toast.success(words);
        reload(words);
      } else reloadKeepingPlace(words);
    } catch (err) {
      running.current = false;
      setBusy(false);
      toast.error(err.message);
    }
  }, [undo, closeToast, reload]);
  useEffect(() => rememberUndo(press, "restore"), [press]);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography component="span" variant="body2" sx={{ flex: "1 1 auto", color: "inherit" }}>
        {message}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        sx={{ minHeight: 36, flexShrink: 0 }}
        aria-keyshortcuts="Control+Z"
        onClick={(event) => {
          event.stopPropagation();
          press();
        }}
      >
        {busy ? "Restoring…" : "Undo"}
      </Button>
    </Box>
  );
};

UndoToast.propTypes = {
  message: PropTypes.node.isRequired,
  undo: PropTypes.shape({ table: PropTypes.string, ids: PropTypes.array }).isRequired,
  closeToast: PropTypes.func,
  reload: PropTypes.func,
};

export default UndoToast;
